import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {fetchFuzzyBrands} from 'api';
import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {CustomHsnCodes} from 'components/pages/DocumentsCreate/InvoiceNew/FormsData/selectOptionsComponents/CustomHsnCodes';
import {useHsnFetching} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useHsnFetching';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {Helper} from 'components/ui-kit/Helper';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {DialogTitle} from 'components/ui-kit/Modal/styled';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {AsyncCreatableSelect, AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {Textarea} from 'components/ui-kit/Textarea';
import {apiClient} from 'core/apiClient';
import {SUPPORT_EMAIL} from 'core/constants/env';
import {PRODUCT_GTIN_STATUS_COLORS} from 'core/constants/statuses';
import {isAdmin$} from 'models/auth';
import {
    editGtinFx,
    createProductFormDefaults$,
    formData$,
    isFormModalOpen$,
    isFormModalOpenChanged,
    setSubmitFx,
    uploadDataFx,
} from 'models/registries/gtinsGrid/upload';
import {colors} from 'theme';
import {UserPermission} from 'types';
import {GtinStatuses, PackageTypes, ProductStatuses} from 'types/gtins';
import {OrganizationRawRow} from 'types/profile';
import {useClipboard, usePermission} from 'utils/custom-hooks';

import {CreateBrandContent} from './CreateBrandContent';
import {getGtinActionPermissions} from './helpers/permissions';
import {useGtinCardControl} from './hooks/useGtinCardControl';
import {BrandNameOption} from './SelectOptions/BrandName';
import {
    ButtonsContainer,
    ButtonsContainerWrapper,
    BooleanFieldControl,
    CopyProductCodeIcon,
    CustomContentContainer,
    Field,
    FieldSectionTitle,
    FormFields,
    ReportBugContainer,
    ReportBugDescription,
    ReportBugHeadline,
    RequiredMark,
    SpinnerLayer,
} from './styled';

type AttributeDataType = 'STRING' | 'TEXT' | 'ENUM' | 'BOOLEAN' | 'INTEGER' | 'DECIMAL';

type AttributeEnumOption = {
    code: string;
    name: string;
};

type AttributeValidation = {
    min?: number | null;
    max?: number | null;
    maxLength?: number | null;
    scale?: number | null;
};

type AttributeConfig = {
    id?: number;
    deploymentCustomer?: number | null;
    fieldSection?: string;
    fieldName: string;
    isVisible: boolean;
    isRequired: boolean;
    isAutoFill: boolean;
    code?: string;
    label?: string;
    section?: string;
    sectionLabel?: string;
    ordinal?: number;
    dataType?: AttributeDataType;
    visible?: boolean;
    required?: boolean;
    autoFill?: boolean;
    editableAfterPublish?: string | boolean | null;
    enumValues?: AttributeEnumOption[];
    validation?: AttributeValidation | null;
};

type FormData = Record<string, any>;

type Country = {
    country: string;
    countryFr: string;
    code: string;
};

type ProductCategory = {
    code: string;
    name: string;
};

type RegistriesData = {
    countries: Country[];
};

const FULL_WIDTH_FIELDS: string[] = ['hsn_description'];

const normalizeAttributesConfig = (fields: AttributeConfig[]): AttributeConfig[] =>
    fields
        .map((field, index) => {
            const fieldName = field.fieldName ?? field.code ?? '';

            return {
                ...field,
                id: field.id ?? index,
                fieldName,
                fieldSection: field.fieldSection ?? field.section ?? '',
                isVisible: field.isVisible ?? field.visible ?? true,
                isRequired: field.isRequired ?? field.required ?? false,
                isAutoFill: field.isAutoFill ?? field.autoFill ?? false,
            };
        })
        .filter((field) => field.fieldName)
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));

const mapEnumOptions = (options: AttributeEnumOption[] = []): SelectOption<string>[] =>
    options.map((option) => ({
        value: option.code,
        label: option.name,
    }));

const isQuantityRequired = (packageType: FormData[string]) => packageType === PackageTypes.GROUP;

const isEditableAfterPublish = (value: AttributeConfig['editableAfterPublish']) => {
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string') return false;

    return ['true', 'yes', 'always', 'editable', 'allowed'].includes(value.toLowerCase());
};

const getDecimalStep = (scale?: number | null) => {
    if (!scale) return 'any';
    return `0.${'0'.repeat(Math.max(scale - 1, 0))}1`;
};

const prepareProductPayload = (
    form: FormData,
    requiredFields: Set<string>,
    booleanFields: Set<string>,
) =>
    Object.entries(form).reduce<FormData>((acc, [field, value]) => {
        if (booleanFields.has(field)) {
            acc[field] = toBooleanValue(value);
            return acc;
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (!trimmedValue && !requiredFields.has(field)) return acc;

            acc[field] = trimmedValue;
            return acc;
        }

        if (value === null || value === undefined) return acc;

        acc[field] = value;
        return acc;
    }, {});

function toBooleanValue(value: unknown) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';

    return !!value;
}

let registriesCache: RegistriesData | null = null;
let registriesRequest: Promise<RegistriesData> | null = null;

const loadRegistries = async (): Promise<RegistriesData> => {
    if (registriesCache) return registriesCache;

    if (!registriesRequest) {
        registriesRequest = Promise.all([
            apiClient.get<Country[]>('service/dict/countries').then(({data}) => data),
        ])
            .then(([countries]) => {
                registriesCache = {countries};
                return registriesCache;
            })
            .finally(() => {
                registriesRequest = null;
            });
    }

    return registriesRequest;
};

export const GtinsUploadForm = () => {
    const {t, i18n} = useTranslation();
    const copy = useClipboard();
    const [loading, setLoading] = useState(false);
    const [brandToCreateName, setBrandToCreateName] = useState('');
    const [isCreateBrandOpen, setIsCreateBrandOpen] = useState(false);
    const formDraftRef = useRef<FormData | null>(null);
    const createDefaultsAppliedRef = useRef(false);
    const lastSchemaParamsRef = useRef<{
        productGroupCode?: FormData[string];
        productCategory?: FormData[string];
    } | null>(null);
    const configRequestIdRef = useRef(0);

    const isOpen = useStore(isFormModalOpen$);
    const editPending = useStore(editGtinFx.pending);
    const editData = useStore(formData$)?.[0];
    const createProductFormDefaults = useStore(createProductFormDefaults$);

    const isAuthor = useMemo(() => !!editData?.is_author, [editData]);
    const isAdmin = !!useStore(isAdmin$);

    const hasEditPermission = usePermission([UserPermission.GTIN_CREATE]);

    const gtinActionPermissions = useMemo(
        () =>
            getGtinActionPermissions({
                status: (editData?.status as GtinStatuses | undefined) ?? undefined,
                isAdmin,
                isAuthor,
                hasEditPermission,
            }),
        [editData?.status, isAdmin, isAuthor, hasEditPermission],
    );

    const FORM_LOCAL_PATH = 'pages.registries.uploadSingleGtin.form.';
    const translateForm = useCallback((key: string) => t(FORM_LOCAL_PATH + 'fields.' + key), [
        i18n.language,
    ]);
    const translateValidates = useCallback(
        (key: string) => t(FORM_LOCAL_PATH + 'validates.' + key),
        [i18n.language],
    );

    const [attributesConfig, setAttributesConfig] = useState<AttributeConfig[]>([]);
    const [productCategoryOptions, setProductCategoryOptions] = useState<SelectOption<string>[]>(
        [],
    );
    const [productCategoryOptionsGroupCode, setProductCategoryOptionsGroupCode] = useState<
        FormData[string] | null
    >(null);
    const [registries, setRegistries] = useState<Partial<RegistriesData>>(registriesCache ?? {});
    const previousProductGroupCodeRef = useRef<FormData[string] | null>(null);

    const getDictionaries = useCallback(async () => {
        if (!isOpen) return;
        setRegistries(await loadRegistries());
    }, [isOpen]);

    const getProductCategories = useCallback(
        async (productGroupCode?: string | null) => {
            if (!productGroupCode) {
                setProductCategoryOptions([]);
                setProductCategoryOptionsGroupCode(null);
                return;
            }

            try {
                const {data} = await apiClient.get<ProductCategory[]>(
                    `service/product-categories/${productGroupCode}`,
                );

                setProductCategoryOptions(
                    data.map((category) => ({
                        value: category.code,
                        label: category.name,
                    })),
                );
                setProductCategoryOptionsGroupCode(productGroupCode);
            } catch (error) {
                setProductCategoryOptions([]);
                setProductCategoryOptionsGroupCode(productGroupCode);
                console.error(error);
            }
        },
        [i18n.language],
    );

    const getFormProducts = (
        overrides: Partial<FormData> = {},
        sourceProducts?: Partial<FormData> | null,
    ) => {
        const values = getValues();
        const products =
            sourceProducts ??
            (Object.keys(values).length
                ? editData
                    ? values
                    : {...createProductFormDefaults, ...values}
                : editData ?? createProductFormDefaults ?? {});

        return {...products, ...overrides};
    };

    const resetInvalidEnumValues = (config: AttributeConfig[], products: FormData) => {
        config.forEach((field) => {
            if (field.dataType !== 'ENUM' || !field.enumValues?.length) return;

            const value = products[field.fieldName];
            if (!value) return;

            const isValidValue = field.enumValues.some((option) => option.code === value);
            if (!isValidValue) {
                setValue(field.fieldName, '', {shouldDirty: true, shouldValidate: true});
            }
        });
    };

    const getSchemaParams = (products: Partial<FormData>) => ({
        productGroupCode: products.product_group_code || undefined,
        productCategory: products.product_category || undefined,
    });

    const isSameSchemaParams = (
        first: {productGroupCode?: FormData[string]; productCategory?: FormData[string]} | null,
        second: {productGroupCode?: FormData[string]; productCategory?: FormData[string]},
    ) =>
        first?.productGroupCode === second.productGroupCode &&
        first?.productCategory === second.productCategory;

    const clearDependentEnumOptions = useCallback(() => {
        setAttributesConfig((config) =>
            config.map((field) => {
                if (
                    field.dataType !== 'ENUM' ||
                    field.fieldName === 'product_group_code' ||
                    field.fieldName === 'product_category'
                ) {
                    return field;
                }

                return {...field, enumValues: []};
            }),
        );
    }, []);

    const getConfig = (
        overrides: Partial<FormData> = {},
        sourceProducts?: Partial<FormData> | null,
        options: {clearInvalidEnums?: boolean; force?: boolean} = {},
    ) => {
        const products = getFormProducts(overrides, sourceProducts);
        const schemaParams = getSchemaParams(products);

        if (!options.force && isSameSchemaParams(lastSchemaParamsRef.current, schemaParams)) {
            return Promise.resolve();
        }

        lastSchemaParamsRef.current = schemaParams;
        const requestId = ++configRequestIdRef.current;

        return apiClient
            .get<AttributeConfig[]>('service/attribute-config/schema/product-cards', {
                params: schemaParams,
            })
            .then(({data}) => {
                if (requestId !== configRequestIdRef.current) return;

                const normalizedConfig = normalizeAttributesConfig(data);
                setAttributesConfig(normalizedConfig);

                if (options.clearInvalidEnums) {
                    resetInvalidEnumValues(normalizedConfig, products);
                }
            });
    };

    const init = async (sourceProducts?: Partial<FormData> | null) => {
        try {
            setLoading(true);
            const products = getFormProducts({}, sourceProducts);
            previousProductGroupCodeRef.current = products.product_group_code ?? null;
            await Promise.all([
                getConfig({}, products, {force: true}),
                getDictionaries(),
                getProductCategories(products.product_group_code),
            ]);
        } catch (error) {
            isFormModalOpenChanged(false);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (editPending) return;
        if (!isOpen) {
            setIsCreateBrandOpen(false);
            setBrandToCreateName('');
            createDefaultsAppliedRef.current = false;
            previousProductGroupCodeRef.current = null;
            lastSchemaParamsRef.current = null;
            configRequestIdRef.current += 1;
            setProductCategoryOptions([]);
            setProductCategoryOptionsGroupCode(null);
            reset(defaultValues);
            return;
        }
        if (isOpen && editData)
            init(editData).then(() => {
                reset(editData);
            });
        else init();
    }, [isOpen, editData, createProductFormDefaults, i18n.language]);

    const validationSchema = useMemo(() => {
        const shape: Record<string, any> = {};
        attributesConfig.forEach((field) => {
            const enumValues =
                field.fieldName === 'product_category'
                    ? productCategoryOptions.map((option) => option.value)
                    : field.enumValues?.map((option) => option.code) ?? [];
            const validation = field.validation ?? {};
            const isNumberField = field.dataType === 'INTEGER' || field.dataType === 'DECIMAL';
            const isBooleanField = field.dataType === 'BOOLEAN';

            let fieldSchema: yup.AnySchema;

            if (isBooleanField) {
                fieldSchema = yup.boolean();
            } else if (isNumberField) {
                let numberSchema = yup
                    .number()
                    .typeError(translateValidates('number'))
                    .nullable()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null ? null : value,
                    );

                if (typeof validation.min === 'number') {
                    numberSchema = numberSchema.min(validation.min, `Min ${validation.min}`);
                }

                if (typeof validation.max === 'number') {
                    numberSchema = numberSchema.max(validation.max, `Max ${validation.max}`);
                }

                if (field.dataType === 'INTEGER') {
                    numberSchema = numberSchema.integer(translateValidates('number'));
                }

                if (typeof validation.scale === 'number') {
                    numberSchema = numberSchema.test(
                        'scale',
                        `Max ${validation.scale} decimal places`,
                        (value) => {
                            if (value === null || value === undefined) return true;
                            const decimals = String(value).split('.')[1]?.length ?? 0;
                            return decimals <= validation.scale!;
                        },
                    );
                }

                fieldSchema = numberSchema;
            } else {
                let stringSchema = yup
                    .string()
                    .nullable()
                    .transform((value) => value || null);

                if (typeof validation.maxLength === 'number') {
                    stringSchema = stringSchema.max(
                        validation.maxLength,
                        `Max ${validation.maxLength} characters`,
                    );
                }

                if (field.dataType === 'ENUM' && enumValues.length) {
                    stringSchema = stringSchema.oneOf(
                        field.isRequired ? enumValues : [...enumValues, null],
                        translateValidates('required'),
                    );
                }

                fieldSchema = stringSchema;
            }

            if (field.fieldName === 'quantity') {
                fieldSchema = fieldSchema.when('package_type', {
                    is: PackageTypes.GROUP,
                    then: (schema) => schema.required(translateValidates('required')),
                    otherwise: (schema) => schema.notRequired(),
                });
            } else if (field.isRequired) {
                fieldSchema = fieldSchema.required(translateValidates('required'));
            }

            shape[field.fieldName] = fieldSchema;
        });

        return yup.object().shape(shape);
    }, [attributesConfig, productCategoryOptions, translateValidates]);

    const defaultValues = useMemo(() => {
        const values: Record<string, string | boolean> = {};
        attributesConfig.forEach((field) => {
            if (field.fieldName === 'status') {
                values[field.fieldName] = GtinStatuses.DRAFT;
                return;
            }

            values[field.fieldName] = field.dataType === 'BOOLEAN' ? false : '';
        });
        return values;
    }, [attributesConfig]);

    const {
        control,
        formState: {errors},
        reset,
        watch,
        setValue,
        setError,
        trigger,
        handleSubmit,
        getValues,
    } = useForm<FormData>({
        resolver: yupResolver(validationSchema),
        defaultValues,
        mode: 'all',
    });

    useEffect(() => {
        if (!isOpen || editData || !createProductFormDefaults) return;
        if (createDefaultsAppliedRef.current) return;

        const registeredDefaultEntries = Object.entries(
            createProductFormDefaults,
        ).filter(([fieldName]) => attributesConfig.some((field) => field.fieldName === fieldName));
        if (registeredDefaultEntries.length !== Object.keys(createProductFormDefaults).length)
            return;

        createDefaultsAppliedRef.current = true;
        reset({...defaultValues, ...createProductFormDefaults});
        registeredDefaultEntries.forEach(([fieldName, value]) => {
            setValue(fieldName, value, {shouldDirty: true, shouldValidate: true});
        });
    }, [
        attributesConfig,
        createProductFormDefaults,
        defaultValues,
        editData,
        isOpen,
        reset,
        setValue,
        trigger,
    ]);

    const taxIdNumber = watch('tax_id_number');
    const fetchLegalName = async () => {
        if (!taxIdNumber) return;
        try {
            const response = await apiClient.get<OrganizationRawRow>(
                `service/dict/legal-identity-address`,
                {params: {taxIdNumber}},
            );

            const {
                legalName,
                address: {address},
            } = response.data;

            if (legalName) setValue('legal_name', legalName);
            if (address) setValue('legal_address', address);
            trigger(['legal_name', 'legal_address']);
        } catch (error) {
            console.error('Fetch legal name error:', error);
        }
    };

    const productGroupCode = watch('product_group_code');
    const productCategory = watch('product_category');
    const packageType = watch('package_type');
    useEffect(() => {
        if (!isOpen || loading || editPending) return;

        const previousProductGroupCode = previousProductGroupCodeRef.current;
        const isProductGroupChanged = previousProductGroupCode !== productGroupCode;

        if (isProductGroupChanged) {
            previousProductGroupCodeRef.current = productGroupCode ?? null;
            setProductCategoryOptions([]);
            setProductCategoryOptionsGroupCode(null);
            clearDependentEnumOptions();

            setValue('product_category', '', {shouldDirty: true, shouldValidate: true});
            setValue('packaging_type', '', {shouldDirty: true, shouldValidate: true});

            Promise.all([
                getProductCategories(productGroupCode),
                getConfig(
                    {
                        product_group_code: productGroupCode ?? '',
                        product_category: '',
                        packaging_type: '',
                    },
                    null,
                    {clearInvalidEnums: true},
                ),
            ]).then(() => editData && trigger());
            return;
        }

        const schemaParams = getSchemaParams({
            product_group_code: productGroupCode,
            product_category: productCategory,
        });
        if (isSameSchemaParams(lastSchemaParamsRef.current, schemaParams)) return;

        clearDependentEnumOptions();
        getConfig({}, null, {clearInvalidEnums: true}).then(() => editData && trigger());
    }, [
        clearDependentEnumOptions,
        productGroupCode,
        productCategory,
        setValue,
        getProductCategories,
    ]);

    useEffect(() => {
        if (packageType !== PackageTypes.UNIT || !getValues('quantity')) return;

        setValue('quantity', '', {shouldDirty: false, shouldValidate: true});
    }, [getValues, packageType, setValue]);

    const saveFormData = useCallback(
        async (form: FormData) => {
            const formSnapshot = {...form, ...getValues()};
            const requiredFields = new Set(
                attributesConfig
                    .filter((field) =>
                        field.fieldName === 'quantity'
                            ? isQuantityRequired(formSnapshot.package_type)
                            : field.isRequired,
                    )
                    .map((field) => field.fieldName),
            );
            const booleanFields = new Set(
                attributesConfig
                    .filter((field) => field.dataType === 'BOOLEAN')
                    .map((field) => field.fieldName),
            );
            const normalizedFormSnapshot = isQuantityRequired(formSnapshot.package_type)
                ? formSnapshot
                : {...formSnapshot, quantity: undefined};
            const productPayload = prepareProductPayload(
                normalizedFormSnapshot,
                requiredFields,
                booleanFields,
            );

            try {
                await uploadDataFx([productPayload]);
                return true;
            } catch (error) {
                const responseData = (error as any).fullErrorsList;
                if (!Array.isArray(responseData)) return false;

                responseData.forEach(({field, error, message}) => {
                    if (!field) return;

                    setError(field, {
                        type: 'manual',
                        message: error ?? message,
                    });
                });

                return false;
            }
        },
        [attributesConfig, getValues, setError],
    );

    const onSubmit = useCallback(async (form: FormData) => saveFormData(form), [saveFormData]);

    const onBeforePublish = useCallback(async () => {
        let isSaved = false;

        await handleSubmit(async (form: FormData) => {
            isSaved = await saveFormData(form);
        }, console.error)();

        return isSaved;
    }, [handleSubmit, saveFormData]);

    const onPublish = useCallback(async () => {
        if (!editData?.product_id) return;
        await setSubmitFx({productId: editData.product_id, status: true});
    }, [editData?.product_id]);

    const hsnLoadOptions = useHsnFetching();
    const gtinCardControl = useGtinCardControl({
        editData,
        onBeforePublish,
        onPublish,
        permissions: gtinActionPermissions,
    });
    const openCreateBrand = useCallback(
        (brandName: string) => {
            formDraftRef.current = getValues();
            setBrandToCreateName(brandName);
            setIsCreateBrandOpen(true);
        },
        [getValues],
    );
    const closeCreateBrand = useCallback(() => {
        if (formDraftRef.current) {
            reset(formDraftRef.current);
        }
        setIsCreateBrandOpen(false);
    }, [reset]);
    const handleBrandCreated = useCallback(
        (brand_name: string) => {
            if (formDraftRef.current) {
                const formDraft = {...formDraftRef.current, brand_name};
                reset(formDraft);
            }
            setValue('brand_name', brand_name, {shouldDirty: true, shouldValidate: true});

            formDraftRef.current = null;
            setBrandToCreateName('');
            setIsCreateBrandOpen(false);
            trigger('brand_name');
        },
        [reset, setValue, trigger],
    );

    const getAttributeLabel = useCallback(
        (attribute: AttributeConfig) => attribute.label || translateForm(attribute.fieldName),
        [translateForm],
    );

    const getAttributeOptions = useCallback(
        (attribute: AttributeConfig, currentValue?: FormData[string]): SelectOption<string>[] => {
            if (attribute.fieldName === 'product_category') {
                const options =
                    productGroupCode && productCategoryOptionsGroupCode !== productGroupCode
                        ? []
                        : productGroupCode && productCategoryOptionsGroupCode === productGroupCode
                        ? productCategoryOptions
                        : mapEnumOptions(attribute.enumValues);

                return options;
            }

            const options = mapEnumOptions(attribute.enumValues);
            if (currentValue && !options.some((option) => option.value === currentValue)) {
                return [{value: currentValue, label: currentValue}, ...options];
            }

            return options;
        },
        [productCategoryOptions, productCategoryOptionsGroupCode, productGroupCode],
    );

    const getIsAttributeRequired = useCallback(
        (attribute: AttributeConfig) =>
            attribute.fieldName === 'quantity'
                ? isQuantityRequired(packageType)
                : attribute.isRequired,
        [packageType],
    );

    const visibleAttributeSections = useMemo(() => {
        const sections: {
            key: string;
            label: string;
            fields: AttributeConfig[];
        }[] = [];

        attributesConfig
            .filter((field) => field.isVisible)
            .filter((field) => {
                if (field.fieldName === 'quantity') return packageType !== PackageTypes.UNIT;
                if (field.fieldName !== 'product_category') return true;
                return getAttributeOptions(field).length > 0;
            })
            .forEach((field) => {
                const key = field.fieldSection || field.section || 'default';
                const existingSection = sections.find((section) => section.key === key);

                if (existingSection) {
                    existingSection.fields.push(field);
                    return;
                }

                sections.push({
                    key,
                    label: field.sectionLabel || key,
                    fields: [field],
                });
            });

        return sections;
    }, [attributesConfig, getAttributeOptions, packageType]);

    const controlRender = (
        controlEvents: ControllerRenderProps<FormData>,
        attribute: AttributeConfig,
    ) => {
        const isDisabledByCreateDefaults =
            !editData &&
            !!createProductFormDefaults &&
            (attribute.fieldName === 'product_type' ||
                (createProductFormDefaults.product_type === 'INTERNAL' &&
                    attribute.fieldName === 'product_code'));
        const isDisabledAfterPublish =
            editData?.status === ProductStatuses.PUBLISHED &&
            !isEditableAfterPublish(attribute.editableAfterPublish);
        const attributeLabel = getAttributeLabel(attribute);
        const baseProps = {
            hasError: !!errors?.[attribute.fieldName],
            placeholder:
                attribute.fieldName === 'category_ved'
                    ? translateValidates('auto')
                    : attributeLabel,
            disabled:
                !gtinActionPermissions.canEdit ||
                isDisabledByCreateDefaults ||
                isDisabledAfterPublish ||
                (attribute.isAutoFill && attribute.fieldName !== 'product_name') ||
                ['category_ved', 'hsn_description', 'status', 'product_type'].includes(
                    attribute.fieldName,
                ),
        };
        const onBlurWithValidates = () => {
            controlEvents.onBlur();
            if (attribute.fieldName === 'tax_id_number') fetchLegalName();
        };

        if (attribute.dataType === 'ENUM' || attribute.fieldName === 'product_category') {
            const options = getAttributeOptions(attribute, controlEvents.value);

            return (
                <div style={{paddingBottom: '2px'}}>
                    <Select
                        value={options.find((o) => o.value === controlEvents.value) ?? null}
                        options={options}
                        onBlur={controlEvents.onBlur}
                        onChange={(option: SelectOption<string> | null) => {
                            controlEvents.onChange(option?.value ?? '');
                            if (attribute.fieldName === 'code_ved') {
                                setValue(
                                    'category_ved',
                                    typeof option?.label === 'string' ? option.label : '',
                                );
                            }
                        }}
                        {...baseProps}
                    />
                </div>
            );
        }

        const dropdownAsyncFields = ['hsn_code', 'brand_name'];
        if (dropdownAsyncFields.includes(attribute.fieldName)) {
            if (attribute.fieldName === 'hsn_code') {
                return (
                    <div style={{paddingBottom: '2px'}}>
                        <AsyncSelect
                            {...baseProps}
                            value={
                                controlEvents.value
                                    ? {
                                          value: controlEvents.value,
                                          label: controlEvents.value,
                                      }
                                    : null
                            }
                            isSearchable={true}
                            defaultOptions={[]}
                            menuPlacement={'bottom'}
                            menuPosition={'fixed'}
                            optionComponent={CustomHsnCodes}
                            loadOptions={hsnLoadOptions}
                            placeholder={'HSN code'}
                            onChange={(option: SelectOption<string>) => {
                                controlEvents.onChange(option.value);
                                setValue('hsn_description', option.label);
                            }}
                        />
                    </div>
                );
            }

            if (attribute.fieldName === 'brand_name') {
                return (
                    <div style={{paddingBottom: '2px'}}>
                        <AsyncCreatableSelect
                            {...baseProps}
                            createButtonLabel={t('pages.registries.createBrand.submit', {
                                defaultValue: 'Create new brand',
                            })}
                            isCreateButtonVisible={(inputValue: string) => !!inputValue.trim()}
                            value={
                                controlEvents.value
                                    ? {
                                          value: controlEvents.value,
                                          label: controlEvents.value,
                                      }
                                    : null
                            }
                            isSearchable={true}
                            defaultOptions={[]}
                            menuPlacement={'top'}
                            menuPosition={'fixed'}
                            onCreateButtonClick={openCreateBrand}
                            optionComponent={BrandNameOption}
                            loadOptions={fetchFuzzyBrands}
                            placeholder={'Brand name'}
                            onChange={(option: SelectOption<string>) =>
                                controlEvents.onChange(option.value)
                            }
                        />
                    </div>
                );
            }
        }

        if (attribute.dataType === 'BOOLEAN') {
            return (
                <BooleanFieldControl>
                    <Checkbox
                        checked={toBooleanValue(controlEvents.value)}
                        onBlur={controlEvents.onBlur}
                        onChange={(event) => controlEvents.onChange(event.target.checked)}
                        disabled={baseProps.disabled}
                    />
                </BooleanFieldControl>
            );
        }

        if (attribute.dataType === 'TEXT') {
            return (
                <Textarea
                    {...baseProps}
                    value={controlEvents.value ?? ''}
                    onChange={controlEvents.onChange}
                    onBlur={onBlurWithValidates}
                />
            );
        }

        const productCodeValue = String(controlEvents.value ?? '');
        const hasProductCode = !!productCodeValue;
        const copyProductCode = () => {
            const productCode = productCodeValue;
            if (!productCode) return;

            copy(productCode);
            toast.success(t('pages.registries.uploadSingleGtin.form.productCodeCopied'));
        };

        const props = {
            ...baseProps,
            ...controlEvents,
            ...((attribute.dataType === 'INTEGER' || attribute.dataType === 'DECIMAL') && {
                type: 'number',
                min: attribute.validation?.min ?? undefined,
                max: attribute.validation?.max ?? undefined,
                step:
                    attribute.dataType === 'INTEGER'
                        ? 1
                        : getDecimalStep(attribute.validation?.scale),
            }),
            ...(attribute.fieldName === 'product_code' && {
                adornment: (
                    <CopyProductCodeIcon
                        $disabled={!hasProductCode}
                        source={CopyIcon}
                        onClick={hasProductCode ? copyProductCode : undefined}
                        fillColor={hasProductCode ? colors.brandPrimary : colors.textMuted}
                        height={16}
                    />
                ),
            }),
        };

        return <Input {...props} onBlur={onBlurWithValidates} />;
    };

    const GPCHelper = () => (
        <Helper
            style={{marginLeft: 5}}
            content={
                <HtmlText
                    __html={t('pages.registries.uploadSingleGtin.helper.gps_code', {
                        link: 'https://gpc-browser.gs1.org/',
                    })}
                />
            }
        />
    );

    const ReportBug = () => (
        <ReportBugContainer>
            <ReportBugHeadline>{t('pages.registries.bugReport.title')}</ReportBugHeadline>
            <ReportBugDescription>
                <HtmlText
                    __html={t('pages.registries.bugReport.description', {
                        link: `mailto:${SUPPORT_EMAIL}`,
                    })}
                />
            </ReportBugDescription>
        </ReportBugContainer>
    );

    const modalFooter = (
        <ButtonsContainerWrapper>
            {editData && editData.status === ProductStatuses.PUBLISHED && <ReportBug />}
            <ButtonsContainer>{gtinCardControl.actionButtons}</ButtonsContainer>
        </ButtonsContainerWrapper>
    );

    const modalSpinner = (
        <SpinnerLayer>
            <Spinner style={{margin: 'auto', height: 75, width: 75}} />
        </SpinnerLayer>
    );

    const defaultModalTitle = (
        <DialogTitle size={4} style={{display: 'flex', gap: 10, alignItems: 'center'}}>
            {editData
                ? t('pages.registries.formGtinTitle')
                : t('pages.registries.uploadFormGtinTitle')}
            <StatusWrapper
                color={PRODUCT_GTIN_STATUS_COLORS[(editData?.status as GtinStatuses) ?? 'DRAFT']}
            >
                {t(`dictionaries.productStatuses.${editData?.status ?? 'DRAFT'}`)}
            </StatusWrapper>
        </DialogTitle>
    );

    const handleCloseModal = useCallback(() => {
        if (isCreateBrandOpen) {
            closeCreateBrand();
            return;
        }

        if (gtinCardControl.isInlineModalOpen) {
            gtinCardControl.closeInlineModal();
            return;
        }

        isFormModalOpenChanged(false);
    }, [closeCreateBrand, gtinCardControl, isCreateBrandOpen]);

    const createBrandContent = (
        <CreateBrandContent
            countries={registries.countries || []}
            initialBrandName={brandToCreateName}
            onBack={closeCreateBrand}
            onCreated={handleBrandCreated}
        />
    );

    const formContent = (
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <FormFields>
                {visibleAttributeSections.map((section) => (
                    <React.Fragment key={section.key}>
                        {section.key !== 'default' && (
                            <FieldSectionTitle>{section.label}</FieldSectionTitle>
                        )}
                        {section.fields.map((attribute) => (
                            <Field
                                $fullWidth={FULL_WIDTH_FIELDS.includes(attribute.fieldName)}
                                key={attribute.id}
                                text={errors?.[attribute.fieldName]?.message}
                                isError={!!errors?.[attribute.fieldName]}
                            >
                                <span style={{display: 'flex', alignItems: 'flex-end'}}>
                                    {getAttributeLabel(attribute)}
                                    {getIsAttributeRequired(attribute) && (
                                        <RequiredMark>*</RequiredMark>
                                    )}
                                    {attribute.fieldName === 'gps_code' && GPCHelper()}
                                </span>
                                <Controller
                                    name={attribute.fieldName}
                                    control={control}
                                    defaultValue={
                                        (createProductFormDefaults as Record<
                                            string,
                                            string
                                        > | null)?.[attribute.fieldName] ??
                                        defaultValues[attribute.fieldName] ??
                                        ''
                                    }
                                    render={(controlEvents) =>
                                        controlRender(controlEvents, attribute)
                                    }
                                />
                            </Field>
                        ))}
                    </React.Fragment>
                ))}
                <Controller
                    name="product_id"
                    control={control}
                    defaultValue={defaultValues['product_id'] ?? ''}
                    render={() => <div style={{display: 'none'}} />}
                />
            </FormFields>
            {modalFooter}
        </form>
    );

    const keepDefaultInlineTitle =
        gtinCardControl.activeInlineModal === 'reject' ||
        gtinCardControl.activeInlineModal === 'unlock';

    let modalTitle: React.ReactNode = defaultModalTitle;
    if (!isCreateBrandOpen && gtinCardControl.isInlineModalOpen) {
        modalTitle = keepDefaultInlineTitle ? defaultModalTitle : gtinCardControl.inlineModalTitle;
    }

    let modalBody: React.ReactNode = formContent;
    if (loading) modalBody = modalSpinner;
    else if (isCreateBrandOpen) modalBody = createBrandContent;
    else if (gtinCardControl.isActionPending) modalBody = modalSpinner;
    else if (gtinCardControl.isInlineModalOpen) modalBody = gtinCardControl.inlineModalContent;

    return (
        <Modal
            show={isOpen}
            onClose={handleCloseModal}
            title={modalTitle}
            titleNoPadding={keepDefaultInlineTitle}
            modalWidth={gtinCardControl.activeInlineModal === 'unlock' ? 0 : undefined}
        >
            <CustomContentContainer>{modalBody}</CustomContentContainer>
        </Modal>
    );
};
